import React, { useEffect } from 'react';
import { Box, Link, Paper, Typography, useTheme } from '@mui/material';
import { toast } from 'react-toastify';

import { SubmitButton } from 'components';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { logout, selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAuthGetEmailVerificationLinkMutation } from 'store/apis/auth.api';

interface VerificationRequestPageProps {}

export const VerificationRequestPage: React.FC<VerificationRequestPageProps> = () => {
  const dispatch = useAppDispatch();
  const { email: userEmail } = useAppSelector(selectCurrentUserInfo);
  const theme = useTheme();
  const [getVerificationLink, { isLoading, isSuccess, isError, error }] = useAuthGetEmailVerificationLinkMutation();

  useEffect(() => {
    if (isError) {
      toast.error((error as any)?.data?.payload?.message);
    }

    if (!isLoading && isSuccess && !isError) {
      toast.success('A verification link has been sent to your email account.');
    }
  }, [isLoading, isSuccess, isError]);

  const handleResendEmail = () => {
    getVerificationLink();
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        paddingY: 12,
        paddingX: 2,
        boxSizing: 'border-box',
      }}
    >
      <Paper
        sx={{
          borderTopWidth: 4,
          borderTopColor: theme.palette.primary.main,
          borderTopStyle: 'solid',
          padding: 2,
          width: 'fit-content',
        }}
      >
        <Box sx={{ margin: 'auto', padding: 2, maxWidth: theme.breakpoints.values.sm }}>
          <Typography variant="h5" sx={{ textAlign: 'center' }}>
            Verify your email address
          </Typography>

          <Typography color="textSecondary" sx={{ marginTop: 1, textAlign: 'center' }}>
            {userEmail}
          </Typography>

          <Typography color="textSecondary" sx={{ marginTop: 4 }}>
            A magic link was sent to your email. Please confirm before we can finish your account.
          </Typography>

          <Typography color="textSecondary" sx={{ marginTop: 2 }}>
            If you have not received an email then click below.
          </Typography>

          <SubmitButton
            loading={isLoading}
            label={'Resend Verification Email'}
            onClick={handleResendEmail}
            style={{ width: '100%' }}
            sx={{ marginTop: 4 }}
          />

          <Typography variant="subtitle1" align="center" color="textSecondary" sx={{ marginTop: 4 }}>
            Or{' '}
            <Link
              component="button"
              variant="subtitle1"
              onClick={() => {
                dispatch(logout());
              }}
            >
              Use another account
            </Link>
          </Typography>

          <Typography variant="subtitle1" align="center" color="textSecondary" sx={{ marginTop: 8 }}>
            Contact{' '}
            <Link
              component="button"
              variant="subtitle1"
              onClick={() => {
                window.location.href = 'mailto:support@mycora.us';
              }}
              sx={{ paddingBottom: 0.2 }}
            >
              support team
            </Link>{' '}
            if you have any questions.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};
